import { useState, useMemo, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { useModelStore } from '../../../state/store/chat/use-model-store';
import { useGetLlmModel } from '../../../hooks/use-llm-model';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from 'hooks/use-mobile';
import { useSubscriptionStore } from 'state/store/subscription/useSubscriptionStore';
import { PLANS } from 'constant/plan';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { MessageRole } from 'features/chat/types/chat';
import { useParams } from 'react-router-dom';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { Asterisk, Box } from 'lucide-react';
import { useTheme } from 'components/core/theme-provider';
import deepseekv32 from 'assets/images/deepseekv3.2.png';
import gptoss from 'assets/images/gptoss120b.png';
import kimik from 'assets/images/kimik2.png';
import llama33 from 'assets/images/llama3.3.png';
import llama4maverick from 'assets/images/llama4maverick.png';
import qwen32 from 'assets/images/qwen32b.png';
import glm45 from 'assets/images/glm4.5.png';







interface ModelSelectorProps {
  isDisabled?: boolean;
}

export const ModelSelector = ({ isDisabled: externalIsDisabled }: ModelSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);

  const selectedModel = useModelStore((state) => state.selectedModel);
  const models = useModelStore((state) => state.models);
  const openNewChatWithModel = useModelStore((state) => state.openNewChatWithModel);
  const userChangedModel = useModelStore((state) => state.userChangedModel);
  const setActiveConversationModelName = useChatStore(
    (state) => state.setActiveConversationModelName
  );

  const { conversationId } = useParams();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  useGetLlmModel();

  const shouldDisableDropdown = useChatStore((state) => {
    if (!conversationId) return false;
    const conv = state.conversations[conversationId];
    const streamingMessage = conv?.messages.find(
      (msg) => msg.recordId === conv?.streamingAssistantMessageId
    );
    return (
      streamingMessage?.role === MessageRole.ASSISTANT ||
      (conv?.activeStreamsMap?.size ?? 0) > 0 ||
      conv?.assistantResponseRemaining !== 0
    );
  });

  const isStreaming = externalIsDisabled || (shouldDisableDropdown && !!conversationId);

  const currentPlan = useSubscriptionStore((state) => state.currentPlan);

  const modelsIds = useMemo(() => models.map((m) => m.id).join(','), [models]);
  const memoizedModels = useMemo(() => models, [modelsIds, models.length]);

  const displayedModels = useMemo(
    () => memoizedModels.slice(0, visibleCount),
    [memoizedModels, visibleCount]
  );

  const displayName = useMemo(() => {
    if (!selectedModel?.name) return '';
    if (isMobile && selectedModel.name.length > 12) {
      return selectedModel.name.slice(0, 12) + '...';
    }
    return selectedModel.name;
  }, [selectedModel?.name, isMobile]);

  const handleModelSelect = useCallback(
    (modelId: string) => {
      if (selectedModel?.id !== modelId) {
        const model = memoizedModels.find((m) => m.id === modelId);
        if (model) {
          setActiveConversationModelName(model.id);
          openNewChatWithModel(model);
          userChangedModel(true);
        }
      }
      setOpen(false);
    },
    [
      memoizedModels,
      openNewChatWithModel,
      selectedModel?.id,
      setActiveConversationModelName,
      userChangedModel,
    ]
  );

  // 🔒 Hook declared BEFORE early return (eslint safe)
  const ensureScrollable = useCallback(
    (el: HTMLDivElement | null) => {
      if (!el) return;
      if (
        el.scrollHeight <= el.clientHeight &&
        visibleCount < memoizedModels.length
      ) {
        setVisibleCount((prev) =>
          Math.min(prev + 5, memoizedModels.length)
        );
      }
    },
    [visibleCount, memoizedModels.length]
  );

  if (
    memoizedModels.length === 0 ||
    !currentPlan ||
    currentPlan.name === PLANS.NOPLAN
  ) {
    return null;
  }

  const modelIconMap: Record<string, string> = {
    'DeepSeek-V3-0324': deepseekv32,
    'Qwen3-32B': qwen32,
    'Qwen2.5-VL-72B-Instruct': qwen32,
    'Llama-3.3-70B-Instruct-fast': llama33,
    'Llama-4-Maverick': llama4maverick,
    'gpt-oss-120b': gptoss,
    'Kimi-K2-Instruct': kimik,
    'GLM-4.5': glm45,
  };

  const modelsIconViewer = (model: any) => {
    const icon = modelIconMap[model.id];
    if (icon) {
      return <img className="w-4 h-4" src={icon} alt={model.name} />;
    }
    return (
      <Asterisk
        className={`w-5 h-5 ${selectedModel?.id === model.id ? 'text-primary' : ''
          }`}
      />
    );
  };

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip open={isStreaming ? undefined : false}>
        <DropdownMenu
          open={isStreaming ? false : open}
          onOpenChange={(isOpen) => {
            if (isStreaming) return;
            setOpen(isOpen);
            if (!isOpen) {
              setVisibleCount(5);
            }
          }}
          modal={false}
        >
          <TooltipTrigger asChild>
            <div>
              <DropdownMenuTrigger asChild disabled={isStreaming}>
                <Button
                  variant="ghost"
                  disabled={isStreaming}
                  className={`rounded-2xl text-primary max-w-[120px] md:max-w-full border-[0.5px] ${theme === 'dark'
                    ? 'border-primary-200'
                    : 'border-primary-600'
                    } w-full justify-between transition-colors duration-200 h-[34px] focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none ${isStreaming ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                >
                  <Box
                    className={`sm:h-4 h-3 sm:w-4 w-3 ${theme === 'dark'
                      ? 'text-primary-200'
                      : 'text-primary-600'
                      }`}
                  />
                  <span
                    className={`text-sm font-medium leading-5 overflow-hidden whitespace-nowrap ${theme === 'dark'
                      ? 'text-primary-200'
                      : 'text-primary-600'
                      }`}
                  >
                    {displayName}
                  </span>
                  <ChevronDown
                    className={`sm:h-4 sm:w-4 h-3 w-3 ${theme === 'dark'
                      ? 'text-primary-200'
                      : 'text-primary-600'
                      }`}
                  />
                </Button>
              </DropdownMenuTrigger>
            </div>
          </TooltipTrigger>

          <DropdownMenuContent
            align="start"
            className={`max-w-[200px] w-[200px] p-0 bg-white border-[0.5px] ${theme === 'dark'
              ? 'border-primary-200'
              : 'border-primary-600'
              } shadow-lg rounded-xl`}
          >
            <div
              ref={ensureScrollable}
              className="max-h-[324px] overflow-y-auto p-4"
              onScroll={(e) => {
                const el = e.currentTarget;
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
                  setVisibleCount((prev) =>
                    Math.min(prev + 5, memoizedModels.length)
                  );
                }
              }}
            >
              {displayedModels.map((model, index) => (
                <DropdownMenuItem
                  key={model.id}
                  className="focus:bg-transparent focus:text-foreground p-0"
                  onSelect={(e) => {
                    e.preventDefault();
                    handleModelSelect(model.id);
                  }}
                >
                  <div className={`flex items-center justify-between w-full hover:cursor-pointer transition-all duration-200 ${index !== 0 ? 'pt-4' : ''
                    } ${index !== displayedModels.length - 1
                      ? `border-b-[0.5px] ${theme === 'dark'
                        ? 'border-border'
                        : 'border-light-gray'
                      } pb-4`
                      : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {modelsIconViewer(model)}
                      <div className="text-sm font-medium leading-5">
                        {model.name}
                      </div>
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Tooltip content - only shows when streaming */}
        {isStreaming && <TooltipContent>
          <div className="text-xs py-1">
            {externalIsDisabled
              ? (t('MODEL_LOCKED_FOR_ASSISTANT') || 'Model selection is locked for this GPT')
              : t('CANT_CHANGE_MODEL_WHILE_STREAMING')
            }
          </div>
        </TooltipContent>}
      </Tooltip>
    </TooltipProvider>
  );
};
